import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-multi-lang";
import "./Table.css";
import { SimpleCheckbox } from "../FormElements/FormElements";
import { WhiteboxLoader } from "../Loader/Loader";

interface DashboardTableProps {
 header: (string | React.ReactNode)[];  body: {
    [key: string]: { [key: string]: any };
  };
  onSelect?: Function;
  loadMore?: Function;
  selectable?: boolean;
  lastpage?: number;
  pagesCount?: number;
}

type TableHandle = {
  reset: () => void;
};

export const DashboardTable = forwardRef<TableHandle, DashboardTableProps>(
  (props, ref) => {
    const t = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const handlePageChange = (page: number) => {
      setCurrentPage(page);
      setIsLoading(true);
      if (props.loadMore) {
        props.loadMore(page);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      } else {
        setIsLoading(false);
      }
    };

    const selectRow = (
      e: React.MouseEvent<HTMLTableRowElement>,
      id?: string
    ) => {
      if (!props.selectable) return;
      e.currentTarget.classList.toggle("active");

      let checkbox: HTMLInputElement | null = e.currentTarget.querySelector(
        "input[type='checkbox']"
      );
      checkbox?.click();

      if (props.onSelect && id) props.onSelect(id);
    };

    useImperativeHandle(ref, () => ({
      reset() {
        setCurrentPage(1);
        if (props.loadMore) props.loadMore(1);
      },
    }));

    const totalPages = props.pagesCount ?? 1;

    // حساب النطاق المحدد من الصفحات لعرضه
    const maxPagesToShow = 5;
    let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 2); // نبدأ من 2 لتجنب إخفاء الصفحة الأولى
    let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages - 1); // لا نعرض الصفحة الأخيرة مباشرة بعد startPage

    // تعديل النطاق إذا كانت الصفحات المتاحة أقل من العدد المراد
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(endPage - maxPagesToShow + 1, 2);
    }

    return (
      <div className="dashboard-table">
        <table>
          {Object.keys(props.body).length === 0 ? (
            ""
          ) : (
            <thead>
              <tr>
                {props.selectable && <th></th>}
                {props.header.map((item, index) => (
                  <th style={item ? {} : { width: "200px" }} key={index}>
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {Object.keys(props.body).length === 0 ? (
            <tbody>
              <tr className="no-items text-center">
                <td colSpan={props.header.length + (props.selectable ? 1 : 0)}>
                  {t("no_items_in_table")}
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {Object.keys(props.body).map((id, tr_index) => (
                <tr
                  key={id}
                  style={{
                    zIndex: Object.keys(props.body).length - tr_index,
                  }}
                  onClick={(e: React.MouseEvent<HTMLTableRowElement>) =>
                    selectRow(e, id)
                  }
                >
                  {props.selectable && (
                    <td width="50">
                      <SimpleCheckbox
                        className="select-row"
                        onClick={(e: React.MouseEvent<HTMLElement>) =>
                          e.stopPropagation()
                        }
                      />
                    </td>
                  )}
                  {Object.keys(props.body[id]).map((key, td_index) => (
                    <td key={tr_index + "_" + td_index}>
                      {props.body[id][key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {/* {isLoading && (
          <div>
            <WhiteboxLoader />
          </div>
        )} */}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            {/* زر السابق */}
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              previous
            </button>

            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(1)}
            >
              1
            </button>

            {/* إذا كانت الصفحات كثيرة نعرض علامة "..." */}
            {startPage > 2 && <span>...</span>}

            {/* صفحات مختصرة */}
            {[...Array(endPage - startPage + 1)].map((_, i) => {
              const page = startPage + i;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={page === currentPage ? "active" : ""}
                >
                  {page}
                </button>
              );
            })}

            {/* إذا كانت الصفحات كثيرة نعرض علامة "..." */}
            {endPage < totalPages - 1 && <span>...</span>}

            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>

            {/* زر التالي */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              next
            </button>
          </div>
        )}
      </div>
    );
  }
);
